import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { LocalizedName } from '@/components/localized-name';
import { useGiftSearch } from '@/features/membership-gifts/gifts.api';
import { GIFT_STATUS } from '@/features/membership-gifts/types';
import { formatDate, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The gifts behind the unredeemed-gifts figure.
 *
 * Its own drawer rather than the subscriptions one, because these rows are not
 * subscriptions: an unredeemed gift is a plan that was paid for and never
 * attached to anybody, so it appears in no membership search at all. Sending
 * this tile to the Subscriptions grid would have shown the opposite set — the
 * gifts that *were* redeemed.
 */

const PAGE_SIZE = 20;

export function GiftRowsSheet({
  locationId,
  startDate,
  endDate,
  open,
  onOpenChange,
}: {
  locationId: string | null;
  startDate: string;
  endDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const query = useGiftSearch(
    {
      searchTerm: '',
      status: GIFT_STATUS.pending,
      page,
      size: PAGE_SIZE,
      locationId,
      startDate,
      endDate,
    },
    open
  );

  const rows = query.data?.gifts ?? [];
  const total = query.data?.total ?? 0;
  // Paging keeps the last page on screen while the next one loads.
  const busy = query.isFetching && !query.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('subscriptionSales.unredeemedGifts')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <p className="text-muted-foreground text-sm">
            {query.isSuccess
              ? t('subscriptionSales.showingOf', { shown: rows.length, total })
              : ' '}
          </p>

          {/* `isLoading`: the query is off while the drawer is closed. */}
          {query.isLoading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-52" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-3 w-48" />
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('subscriptionSales.loadFailed')}
            </p>
          )}

          {query.isSuccess && rows.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                {t('subscriptionSales.noRows')}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="relative rounded-xl">
                <BusyOverlay show={busy} />
                <div
                  className={cn(
                    'divide-y rounded-xl border',
                    busy && 'opacity-60 transition-opacity'
                  )}
                >
                  {rows.map((gift) => (
                    <div key={gift.id} className="space-y-1.5 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {gift.recipientFullName ?? '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {gift.recipientEmail}
                          </p>
                          {gift.recipientPhone && (
                            <p className="text-muted-foreground truncate text-xs tabular-nums">
                              {formatPhone(gift.recipientPhone)}
                            </p>
                          )}
                        </div>
                        <Badge variant="warning">
                          {t('subscriptionSales.notRedeemed')}
                        </Badge>
                      </div>

                      <LocalizedName
                        nameEn={gift.subscription?.nameEn}
                        nameEs={gift.subscription?.nameEs}
                        internalName={gift.subscription?.internalName}
                      />
                      <p className="text-muted-foreground text-xs">
                        {t('subscriptionSales.boughtBy')}{' '}
                        {gift.buyer?.completeName ?? '—'} ·{' '}
                        {formatDate(gift.createdAt)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <Pagination
                page={page}
                size={PAGE_SIZE}
                total={total}
                onPageChange={setPage}
              />
            </>
          )}

          <Button variant="outline" className="w-full" asChild>
            <Link
              to={`/booking/membership-gifts?status=${GIFT_STATUS.pending}`}
            >
              <ExternalLink />
              {t('subscriptionSales.openInGifts')}
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
